/**
 * Dataset별 마지막 episode의 로더·화면·재생 위치를 보존한다.
 * 섹션 이동은 Parquet을 유지하고 episode 교체나 상세 닫기에서 자원을 해제한다.
 * 매핑 편집본은 Revision별 MappingEditor, 실행 중인 공통 시계는 c6가 소유한다.
 *
 * 같은 Dataset 을 창과 route 페이지가 함께 열 수 있다. 그래서 상세마다 이 Dataset 을 잡고(`holdInspectDataset`),
 * 마지막 상세가 놓을 때만 세션과 매핑 편집 상태를 버린다. 한쪽을 닫아도 다른 쪽이 보는 상태는 남는다.
 */
import { Holders } from "@/shared/utils/holders";
import { EpisodeInspectLoader, type EpisodeInspectLoaderOptions } from "./episodeInspectLoader";
import { disposeMappingEditors, hasUnsavedDatasetMappingEdits } from "./mappingEditor";

/** 우측 패널은 편집·원본·실행 결과를 각각 보여 준다. */
export type InspectSidePanel = "mcp" | "mapping" | "channels" | "validation" | "poc";
export type InspectMediaPanel = "sim" | "videos";

export type InspectView = {
  readonly sidePanel: InspectSidePanel;
  readonly mediaPanel: InspectMediaPanel;
  readonly mediaLayout: "pip" | "split" | "overlay";
  readonly cameraKeys: readonly string[];
  readonly meshOpacity: number;
  readonly reviewPanelOpen: boolean;
  readonly compactTab: "media" | "panel";
};

export type InspectSessionOptions = EpisodeInspectLoaderOptions & { datasetUuid?: string };

export function inspectSessionKey(options: { probeUuid: string; sourceId: string }): string {
  return `${options.probeUuid}:${options.sourceId}`;
}

export class InspectSession {
  readonly key: string;
  readonly loader: EpisodeInspectLoader;
  playback = { currentTimeSeconds: 0, playbackRate: 1, playing: false };
  private view: InspectView;
  private readonly listeners = new Set<() => void>();
  private refreshedExpiry: number | null = null;

  constructor(options: InspectSessionOptions) {
    this.key = inspectSessionKey(options);
    this.loader = new EpisodeInspectLoader(options);
    this.view = {
      sidePanel: "mapping",
      mediaPanel: "videos",
      mediaLayout: "split",
      cameraKeys: [],
      meshOpacity: 1,
      reviewPanelOpen: true,
      compactTab: "media",
    };
    void this.loader.init();
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  readonly getSnapshot = (): InspectView => this.view;

  setSidePanel(sidePanel: InspectSidePanel): void {
    this.update({ sidePanel, compactTab: "panel", reviewPanelOpen: true });
  }

  setMediaPanel(mediaPanel: InspectMediaPanel): void {
    this.update({ mediaPanel });
  }

  setMediaLayout(mediaLayout: InspectView["mediaLayout"]): void {
    this.update({ mediaLayout });
  }
  setCameras(keys: readonly string[]): void {
    const unique = [...new Set(keys)];
    if (unique.length > 2) return;
    // 드롭다운은 옵션 순서로 반환하므로 남아 있는 선택의 좌우 순서를 보존한다.
    const retained = this.view.cameraKeys.filter((key) => unique.includes(key));
    this.update({ cameraKeys: [...retained, ...unique.filter((key) => !retained.includes(key))] });
  }
  setMeshOpacity(meshOpacity: number): void {
    if (!Number.isFinite(meshOpacity)) return;
    this.update({ meshOpacity: Math.min(1, Math.max(0, meshOpacity)) });
  }
  setReviewPanelOpen(reviewPanelOpen: boolean): void {
    this.update({ reviewPanelOpen });
  }
  setCompactTab(compactTab: InspectView["compactTab"]): void {
    this.update({ compactTab });
  }

  /**
   * 이 만료 시각으로 아직 갱신하지 않았는지 답한다. 답하면서 갱신했다고 표시한다.
   *
   * 서버가 이미 만료된 URL 을 되풀이하면 요청이 끝없이 돈다. 화면이 다시 마운트돼도 답이 같아야
   * 하므로 세션이 이 표시를 가진다.
   */
  shouldRefreshMedia(expiresAtMs: number): boolean {
    if (this.refreshedExpiry === expiresAtMs) {
      return false;
    }
    this.refreshedExpiry = expiresAtMs;
    return true;
  }

  dispose(): void {
    this.loader.dispose();
    this.listeners.clear();
  }

  private update(next: Partial<InspectView>): void {
    this.view = { ...this.view, ...next };
    for (const listener of this.listeners) {
      listener();
    }
  }
}

// Dataset 마다 마지막 episode 하나를 유지한다.
const sessions = new Map<string, InspectSession>();
const holders = new Holders<string>({ last: disposeDataset });

function disposeDataset(datasetUuid: string): void {
  sessions.get(datasetUuid)?.dispose();
  sessions.delete(datasetUuid);
  disposeMappingEditors(datasetUuid);
}

/**
 * 이 episode 의 세션을 얻는다. 같은 episode 면 쓰던 세션을 주고, 다르면 새로 만든다.
 *
 * 다른 episode 를 열면 이전 세션을 버린다. 이때만 Parquet 을 놓아도 된다.
 */
export function acquireInspectSession(options: InspectSessionOptions): InspectSession {
  const key = inspectSessionKey(options);
  const owner = options.datasetUuid ?? "renderer";
  const existing = sessions.get(owner);
  if (existing !== undefined && existing.key === key) {
    return existing;
  }
  existing?.dispose();
  const session = new InspectSession(options);
  sessions.set(owner, session);
  return session;
}

/**
 * 상세 하나가 이 Dataset 의 점검 세션과 매핑 편집 상태를 잡는다. 돌려준 함수가 놓는다. 마지막 상세가 놓으면 둘 다
 * 버리고, 다음 `acquire` 는 처음부터 받는다.
 */
export function holdInspectDataset(datasetUuid: string): () => void {
  return holders.hold(datasetUuid);
}

/**
 * 이 상세가 놓으면 잃는 매핑 편집이 있는지 — 저장하지 않은 편집이 있고, 이 Dataset 을 잡은 상세가 이 하나뿐이다.
 * 다른 상세가 같은 Dataset 을 잡고 있으면 편집은 그쪽에 남는다. 창의 닫기 guard 와 route 페이지의 이동 guard 가 쓴다.
 */
export function wouldLoseDatasetMappingEdits(datasetUuid: string): boolean {
  return holders.count(datasetUuid) <= 1 && hasUnsavedDatasetMappingEdits(datasetUuid);
}

/** 세션 끝과 테스트 사이에 부른다. 잡은 상세와 관계없이 세션과 매핑 편집 상태를 모두 버린다. */
export function disposeInspectSessions(): void {
  for (const session of sessions.values()) session.dispose();
  sessions.clear();
  disposeMappingEditors();
  holders.clear();
}
